import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useThemeContext } from "../../globalContext/global-context";
import type { SurveyAttempt } from "../../session/SurveySession";
import type { HostControlBindings } from "../contracts";
import {
  gridQuestionGroupCore,
  isGridQuestionGroupRaw,
} from "../gridQuestionGroup/core";
import { gridQuestionGroupModule } from "../gridQuestionGroup/module";
import { GridQuestionGroupLayout } from "../gridQuestionGroup/views";
import {
  activeQuestionGroupChild,
  isQuestionGroupContainerRaw,
  questionGroupChildRaw,
  questionGroupCore,
} from "../questionGroup/core";
import { questionGroupModule } from "../questionGroup/module";
import { questionRegistry } from "../registry";
import { groupForValue } from "../visualGroups";
import { ActiveQuestion } from "./ActiveQuestion";
import type { AttemptDraftState, SessionQuestionSlotProps } from "./types";
import { groupDraftScopeKey, groupDraftValue } from "./util/draftState";
import { prepareGroupSubmission } from "./util/groupSubmission";

const TimedChildGate = ({
  durationSeconds,
  children,
}: {
  durationSeconds: number;
  children: (gate: { availableAtMs: number; ready: boolean }) => ReactNode;
}) => {
  const [availableAtMs] = useState(() => Date.now() + durationSeconds * 1000);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const timeout = setTimeout(
      () => setReady(true),
      Math.max(0, availableAtMs - Date.now()),
    );
    return () => clearTimeout(timeout);
  }, [availableAtMs]);
  return children({ availableAtMs, ready });
};

export const ActiveQuestionGroup = (
  props: SessionQuestionSlotProps & {
    attempt: SurveyAttempt;
  },
) => {
  const { globalObject } = useThemeContext();
  const [localDraftState, setLocalDraftState] =
    useState<AttemptDraftState | null>(null);
  const [timedAcknowledgements, setTimedAcknowledgements] = useState<{
    attemptId: number;
    childIds: ReadonlySet<string>;
  }>({ attemptId: props.attempt.id, childIds: new Set() });
  const [automaticReadiness, setAutomaticReadiness] = useState<{
    attemptId: number;
    childIds: ReadonlySet<string>;
  }>({ attemptId: props.attempt.id, childIds: new Set() });
  const questionRaw = props.attempt.question.question;
  /* v8 ignore next -- SessionQuestionSlot dispatches here only for question groups */
  const raw = isQuestionGroupContainerRaw(questionRaw) ? questionRaw : null;
  const pendingReadinessChildren = useMemo(
    () =>
      props.GroupFrame && raw
        ? raw.children.filter(
            (child) =>
              !child.answered &&
              (child.question.auto_response === "ok" ||
                (child.question.type !== "image" &&
                  Number(child.question.number_seconds) > 0)),
          )
        : [],
    [props.GroupFrame, raw],
  );
  useEffect(() => {
    const timers = pendingReadinessChildren.map((child) => {
      const seconds = Number(child.question.number_seconds);
      const availableAtMs =
        props.attempt.startedAtMs + Math.max(0, seconds) * 1000;
      return setTimeout(
        () => {
          setAutomaticReadiness((current) => {
            const childIds = new Set(
              /* v8 ignore next -- stale timer callbacks are cancelled on attempt changes */
              current.attemptId === props.attempt.id ? current.childIds : [],
            );
            childIds.add(child.id);
            return { attemptId: props.attempt.id, childIds };
          });
        },
        Math.max(0, availableAtMs - Date.now()),
      );
    });
    return () => timers.forEach(clearTimeout);
  }, [pendingReadinessChildren, props.attempt.id, props.attempt.startedAtMs]);
  /* v8 ignore next -- SessionQuestionSlot dispatches here only for question groups */
  if (!raw) return null;
  const gridDefinition = isGridQuestionGroupRaw(raw)
    ? gridQuestionGroupCore.decode(raw)
    : null;
  const definition = gridDefinition
    ? gridDefinition
    : questionGroupCore.decode({ ...raw, type: "question_group" });
  const isGrid = gridDefinition !== null;
  const scopeKey = groupDraftScopeKey(
    definition.groupId,
    props.attempt.question.sequence_id,
  );
  const copy = globalObject.themeData.normalizedTheme.copy;
  const draftState = props.draftState ?? localDraftState;
  /* v8 ignore next -- direct slot consumers may omit the shared draft setter */
  const setDraftState = props.setDraftState ?? setLocalDraftState;

  if (!props.GroupFrame) {
    const child = activeQuestionGroupChild(raw);
    /* v8 ignore next -- malformed active IDs are rejected by the group decoder */
    if (!child) return null;
    const module = questionRegistry.get(child.question.type);
    /* v8 ignore next -- group child modules are checked by supportsSurveyQuestion */
    if (!module)
      throw new Error(
        `Unsupported survey question type: ${child.question.type}`,
      );
    return (
      <ActiveQuestion
        {...props}
        attempt={props.attempt}
        module={module}
        raw={questionGroupChildRaw(child)}
        groupContext={
          child.row === undefined
            ? undefined
            : groupForValue(gridDefinition?.rowGroups, child.row)?.text
        }
        draftId={child.id}
        draftScope={scopeKey}
        answered={child.answered}
        view={undefined}
      />
    );
  }

  const { Frame: GroupFrame, ItemFrame } = props.GroupFrame;
  const childValidity = new Map(
    definition.children.map((child) => {
      if (child.answered || child.question.auto_response === "ok") {
        return [child.id, true] as const;
      }
      const module = questionRegistry.get(child.question.type);
      /* v8 ignore next -- group child modules are checked by supportsSurveyQuestion */
      if (!module) return [child.id, false] as const;
      const childDefinition = module.core.decode(questionGroupChildRaw(child));
      const draft = groupDraftValue(
        draftState,
        props.session,
        scopeKey,
        child,
        module,
        childDefinition,
      );
      return [
        child.id,
        module.core.validate(childDefinition, draft, copy).valid,
      ] as const;
    }),
  );
  const childCompletion = new Map(
    definition.children.map(
      (child) =>
        [
          child.id,
          child.answered ||
            (!child.question.error_message &&
              childValidity.get(child.id) === true),
        ] as const,
    ),
  );
  const pendingChildren = definition.children.filter(
    (child) => !child.answered,
  );
  /* v8 ignore next -- acknowledgement state is scoped to the mounted attempt */
  const acknowledgedTimedChildIds =
    timedAcknowledgements.attemptId === props.attempt.id
      ? timedAcknowledgements.childIds
      : new Set<string>();
  /* v8 ignore next -- readiness state is scoped to the mounted attempt */
  const readyAutomaticChildIds =
    automaticReadiness.attemptId === props.attempt.id
      ? automaticReadiness.childIds
      : new Set<string>();
  const activeTimedChild = pendingChildren.find(
    (child) =>
      child.question.type === "image" &&
      Number(child.question.number_seconds) > 0 &&
      !acknowledgedTimedChildIds.has(child.id),
  );
  const acknowledgeTimedChild = (childId: string) => {
    setTimedAcknowledgements((current) => {
      const childIds = new Set(
        /* v8 ignore next -- stale timed-child callbacks are cancelled on attempt changes */
        current.attemptId === props.attempt.id ? current.childIds : [],
      );
      childIds.add(childId);
      return { attemptId: props.attempt.id, childIds };
    });
  };
  const allValid = pendingChildren.every(
    (child) => childValidity.get(child.id) === true,
  );
  const allDelayedChildrenReady = pendingReadinessChildren.every((child) =>
    readyAutomaticChildIds.has(child.id),
  );
  const snapshot = props.session.getSnapshot();
  const requestedAction = isGrid
    ? gridQuestionGroupModule.controls(
        gridDefinition!,
        gridQuestionGroupCore.initialize(gridDefinition!),
        { view: "default", arrangement: props.arrangement },
        copy,
      ).primaryAction
    : questionGroupModule.controls(
        definition,
        questionGroupCore.initialize(definition),
        { view: "default", arrangement: props.arrangement },
        copy,
      ).primaryAction;
  /* v8 ignore next -- the question-group module contract always declares its group action */
  if (!requestedAction) {
    throw new Error("Question group module requires a primary action.");
  }
  const actionDisabled =
    Boolean(requestedAction.disabled) ||
    !snapshot.canSubmit ||
    !allValid ||
    !allDelayedChildrenReady ||
    activeTimedChild !== undefined ||
    pendingChildren.length === 0;
  const controls: HostControlBindings = {
    canSubmit: snapshot.canSubmit,
    primaryAction: {
      purpose: requestedAction.purpose,
      label: requestedAction.label,
      disabled: actionDisabled,
      pending: snapshot.pending !== null || Boolean(requestedAction.pending),
      run: () => {
        if (actionDisabled) return;
        const prepared = prepareGroupSubmission(
          props.attempt.question,
          draftState,
          props.session,
          copy,
        );
        /* v8 ignore next -- the enabled action has pending, valid, supported children */
        if (!prepared) return;
        props.session.submitGroup(props.attempt, definition.groupId, prepared);
      },
    },
  };
  return (
    <GroupFrame
      attemptId={props.attempt.id}
      heading={definition.heading}
      error={raw.group_error_message}
      controls={controls}
      containerType={raw.type}
    >
      {(() => {
        const items = definition.children.map((child) => {
          const module = questionRegistry.get(child.question.type);
          /* v8 ignore next -- group child modules are checked by supportsSurveyQuestion */
          if (!module)
            throw new Error(
              `Unsupported survey question type: ${child.question.type}`,
            );
          const timedImage =
            child.question.type === "image" &&
            Number(child.question.number_seconds) > 0;
          const activeTimed = child.id === activeTimedChild?.id;
          const question = (
            <ActiveQuestion
              {...props}
              key={child.id}
              attempt={props.attempt}
              Frame={ItemFrame}
              module={module}
              raw={questionGroupChildRaw(child)}
              draftId={child.id}
              draftScope={scopeKey}
              answered={child.answered}
              correctionActive={
                activeTimed ||
                (!timedImage && child.id === definition.activeChildId)
              }
              deferSubmission
              draftState={draftState}
              setDraftState={setDraftState}
              promptInHistory={false}
              history={undefined}
              view={undefined}
            />
          );
          return {
            childId: child.id,
            row: child.row ?? "",
            field: child.field,
            complete: childCompletion.get(child.id) === true,
            content: activeTimed ? (
              <TimedChildGate
                key={child.id}
                durationSeconds={Number(child.question.number_seconds)}
              >
                {({ availableAtMs, ready }) => (
                  <ActiveQuestion
                    {...props}
                    attempt={props.attempt}
                    Frame={ItemFrame}
                    module={module}
                    raw={questionGroupChildRaw(child)}
                    draftId={child.id}
                    draftScope={scopeKey}
                    answered={child.answered}
                    correctionActive
                    deferSubmission
                    deferredCanSubmit={ready}
                    deferredAvailableAtMs={availableAtMs}
                    onDeferredSubmit={() => acknowledgeTimedChild(child.id)}
                    draftState={draftState}
                    setDraftState={setDraftState}
                    promptInHistory={false}
                    history={undefined}
                    view={undefined}
                  />
                )}
              </TimedChildGate>
            ) : (
              question
            ),
          };
        });
        return isGrid ? (
          <GridQuestionGroupLayout
            definition={gridDefinition!}
            arrangement={props.arrangement}
            items={items}
          />
        ) : (
          items.map((item) => item.content)
        );
      })()}
    </GroupFrame>
  );
};
